import type { ScoringResult, Question, Answer } from '@/types';

export interface ResultsSummaryOptions {
  companyName: string;
  scoring: ScoringResult;
  nonScoringCategories?: string[];
  showDownloadButton?: boolean; // defaults to true; false hides the button (e.g. franqueabilidade)
  showContactHighlight?: boolean; // shows prominent "1 dia útil" contact banner (franqueabilidade)
  questions?: Question[]; // used to render per-question improvement CTAs
  answers?: Answer[];     // used to check which improvable questions scored below max
  onDownload: () => void;
  isDownloading?: boolean;
}

export class ResultsSummary {
  private container: HTMLElement;
  private options: ResultsSummaryOptions;

  constructor(parentElement: HTMLElement, options: ResultsSummaryOptions) {
    this.options = options;
    this.container = document.createElement('div');
    this.container.className = 'result-container';

    this.render();
    parentElement.appendChild(this.container);

    // Trigger confetti if score >= 80%
    if (options.scoring.totalPercentage >= 80) {
      this.triggerConfetti();
    }
  }

  private triggerConfetti(): void {
    const canvas = document.createElement('canvas');
    canvas.id = 'confetti-canvas';
    canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Particle[] = [];
    const colors = ['#2563eb', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      size: number;
      rotation: number;
      rotationSpeed: number;
    }

    // Create particles
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20,
        vx: (Math.random() - 0.5) * 8,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 5,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10
      });
    }

    let animationFrame: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let activeParticles = 0;

      for (const p of particles) {
        p.y += p.vy;
        p.x += p.vx;
        p.vy += 0.1; // gravity
        p.rotation += p.rotationSpeed;

        if (p.y < canvas.height + 50) {
          activeParticles++;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size / 2);
          ctx.restore();
        }
      }

      if (activeParticles > 0) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        canvas.remove();
      }
    };

    animate();

    // Remove canvas after 5 seconds
    setTimeout(() => {
      cancelAnimationFrame(animationFrame);
      canvas.remove();
    }, 5000);
  }

  private render(): void {
    const { companyName, scoring } = this.options;

    const scoreClass = scoring.totalPercentage >= 70 ? 'high' :
                       scoring.totalPercentage >= 50 ? 'medium' : 'low';

    const showCelebration = scoring.totalPercentage >= 80;

    this.container.innerHTML = `
      ${showCelebration ? `
        <div class="celebration-banner">
          <span class="celebration-icon">🎉</span>
          <span>Parabéns! Seu negócio tem alto potencial de franqueabilidade!</span>
          <span class="celebration-icon">🎉</span>
        </div>
      ` : ''}

      <div class="result-header">
        <img src="/assets/inova-hub-franquias.png" alt="Inova Franquias" class="logo">
        <h1>Diagnóstico Concluído!</h1>
        <p class="company-name">${companyName}</p>
      </div>

      <div class="score-display">
        <div class="score-value ${scoreClass}">${scoring.totalPercentage}%</div>
        <div class="score-label">Score da Analise</div>
        <span class="recommendation-badge ${scoreClass}">${scoring.finalRecommendation}</span>
      </div>

      <div class="categories-summary">
        <h3>Análise por Categoria</h3>
        ${scoring.categoryScores.filter(cat => !(this.options.nonScoringCategories ?? ['indicadores_financeiros']).includes(cat.category)).map(cat => `
          <div class="category-item">
            <span class="category-status ${cat.status}"></span>
            <span class="category-name">${cat.label}</span>
            <span class="category-score">${cat.percentage}%</span>
          </div>
        `).join('')}
      </div>

      ${(this.options.showDownloadButton ?? true) ? `
      <button class="btn-download" id="btn-download">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>Baixar Relatório Completo (PDF)</span>
      </button>
      ` : ''}

      ${scoring.detailedAnalysis.length > 0 ? `
        <div class="analysis-items">
          <h3>Resumo da Análise</h3>
          ${scoring.detailedAnalysis.map(item => `
            <div class="analysis-item">${item}</div>
          `).join('')}
        </div>
      ` : ''}

      ${this.renderImprovementOpportunities()}

      <div class="analysis-items" style="margin-top: 24px;">
        <h3>Próximos Passos</h3>
        ${this.options.showContactHighlight ? `
          <div style="
            background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
            border-radius: 12px;
            padding: 20px 24px;
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 12px;
            box-shadow: 0 4px 12px rgba(37,99,235,0.3);
          ">
            <span style="font-size: 2rem; flex-shrink: 0;">📞</span>
            <div>
              <p style="color: #fff; font-weight: 700; font-size: 1.1rem; margin: 0 0 4px 0;">
                Você receberá um contato em até 1 dia útil!
              </p>
              <p style="color: #bfdbfe; font-size: 0.9rem; margin: 0;">
                Nossa equipe especializada irá apresentar o caminho ideal para franquear o seu negócio.
              </p>
            </div>
          </div>
        ` : `
          <div class="analysis-item">
            Nossa equipe entrará em contato para apresentar como a Inova Franquias pode ajudar
            você em seus objetivos!
          </div>
        `}
      </div>

      <div class="video-section">
        <h3>Conheça a Inova Franquias</h3>
        <p class="video-description">Assista ao vídeo e conheça mais sobre nós.</p>
        <div class="video-wrapper">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/3ZS-YiliDC4"
            title="Inova Franquias"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen>
          </iframe>
        </div>
      </div>

      <div class="inova-section">
        <div class="inova-stats">
          <div class="stat-item">
            <span class="stat-number">+700</span>
            <span class="stat-label">Marcas Formatadas</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">+4000</span>
            <span class="stat-label">Unidades Vendidas</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">+10</span>
            <span class="stat-label">Anos no Brasil</span>
          </div>
        </div>
        <p class="inova-rating">★ Nota máxima em avaliações pelos clientes ★</p>
      </div>

      <a href="https://inovafranquias.com.br" target="_blank" rel="noopener noreferrer" class="btn-inova">
        <span>Visite nosso site</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
          <polyline points="15 3 21 3 21 9"></polyline>
          <line x1="10" y1="14" x2="21" y2="3"></line>
        </svg>
      </a>

      <div style="text-align: center; margin-top: 24px; padding-bottom: 8px;">
        <a href="#" id="btn-debug-download" style="color: #ccc; font-size: 0.72rem; text-decoration: none;">
          baixar pdf
        </a>
      </div>
    `;

    const downloadBtn = this.container.querySelector('#btn-download') as HTMLButtonElement | null;
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => {
        this.options.onDownload();
      });
    }

    const debugLink = this.container.querySelector('#btn-debug-download') as HTMLAnchorElement | null;
    if (debugLink) {
      debugLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.options.onDownload();
      });
    }
  }

  private renderImprovementOpportunities(): string {
    const { questions, answers } = this.options;
    if (!questions || !answers) return '';

    const improvableItems = questions.filter(q =>
      q.improvable === true &&
      q.maxScore > 0
    ).map(q => {
      const answer = answers.find(a => a.questionId === q.id);
      return { question: q, score: answer?.score ?? 0 };
    }).filter(item => item.score < item.question.maxScore);

    if (improvableItems.length === 0) return '';

    const cards = improvableItems.map(({ question, score }) => {
      const shortText = question.text.length > 70
        ? question.text.slice(0, 67) + '...'
        : question.text;
      const pct = Math.round((score / question.maxScore) * 100);
      return `
        <div style="
          background: #fff;
          border: 1px solid #e2e8f0;
          border-left: 4px solid #2563eb;
          border-radius: 8px;
          padding: 14px 16px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        ">
          <div style="flex: 1; min-width: 0;">
            <p style="margin: 0 0 4px 0; font-size: 0.85rem; color: #64748b;">${shortText}</p>
            <p style="margin: 0; font-size: 0.8rem; color: #94a3b8;">Score atual: ${pct}% — pode chegar a 100% com a Inova</p>
          </div>
          <a
            href="https://inovafranquias.com.br"
            target="_blank"
            rel="noopener noreferrer"
            style="
              flex-shrink: 0;
              background: #2563eb;
              color: #fff;
              border-radius: 6px;
              padding: 8px 14px;
              font-size: 0.8rem;
              font-weight: 600;
              text-decoration: none;
              white-space: nowrap;
            "
          >Fale com a Inova</a>
        </div>
      `;
    }).join('');

    return `
      <div class="analysis-items" style="margin-top: 24px;">
        <h3>Oportunidades de Melhoria com a Inova</h3>
        <p style="font-size: 0.9rem; color: #64748b; margin: 0 0 16px 0;">
          Os critérios abaixo podem ser elevados ao máximo com o apoio da nossa consultoria especializada.
        </p>
        ${cards}
      </div>
    `;
  }

  setDownloading(isDownloading: boolean): void {
    const btn = this.container.querySelector('#btn-download') as HTMLButtonElement;
    if (btn) {
      btn.disabled = isDownloading;
      const span = btn.querySelector('span');
      if (span) {
        span.textContent = isDownloading ? 'Gerando PDF...' : 'Baixar Relatório Completo (PDF)';
      }
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
